import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { Heart, MessageCircle, Send, Trash2, Edit2, Check, MoreVertical, Flag, Trophy, Star } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Input } from './ui/input';
import { useAuth } from '../context/AuthContext';
import SimpleVideoPlayer from './SimpleVideoPlayer';
import ExpandableText from './ExpandableText';
import { BACKEND_URL, socialAPI } from '../services/api';
import UserListModal from './UserListModal';

const PostCard = ({ post, onLike, onComment, onDeleteComment, onVote, onDelete, onEdit, onReport, onFeature }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [showComments, setShowComments] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [comments, setComments] = useState(post.comments || []);
    const [submitting, setSubmitting] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editedCaption, setEditedCaption] = useState(post.caption || '');
    const [showMenu, setShowMenu] = useState(false);
    const [likesModal, setLikesModal] = useState({ isOpen: false, users: [], loading: false });

    const handleSubmitComment = async (e) => {
        e.preventDefault();
        if (!newComment.trim() || submitting) return;

        setSubmitting(true);
        const result = await onComment(post.id, newComment);
        if (result) {
            setComments([...comments, result]);
            setNewComment('');
        }
        setSubmitting(false);
    };

    const handleDeleteComment = async (commentId) => {
        const success = await onDeleteComment(commentId);
        if (success) {
            setComments(comments.filter(c => c.id !== commentId));
        }
    };

    const handleSaveEdit = async () => {
        if (!editedCaption.trim()) return;
        const success = await onEdit(post.id, { caption: editedCaption });
        if (success) {
            setIsEditing(false);
        }
    };

    const handleCancelEdit = () => {
        setEditedCaption(post.caption || '');
        setIsEditing(false);
    };

    const handleShowLikes = async () => {
        if (post.like_count === 0) return;
        setLikesModal({ isOpen: true, users: [], loading: true });
        try {
            const response = await socialAPI.getLikes(post.id);
            setLikesModal({ isOpen: true, users: response.data, loading: false });
        } catch (error) {
            console.error('Failed to load likes:', error);
            setLikesModal(prev => ({ ...prev, loading: false }));
        }
    };

    const isTeamPost = !!post.team_id;
    const authorName = post.author?.username;
    const authorAvatar = post.author?.avatar_url;
    const authorLink = isTeamPost ? `/teams/${post.team_id}/profile` : `/profile/${post.author?.username}`;

    return (
        <Card className="max-lg:bg-[#0a0a0b] lg:bg-[#1a1a1b] border-y lg:border border-white/[0.05] lg:border-gray-800 lg:rounded-xl rounded-none lg:mb-4 mb-3 shadow-2xl lg:shadow-sm">
            <CardContent className="p-0 lg:p-4">
                {/* Author Header */}
                <div className="flex items-center justify-between p-3 lg:p-0 mb-0 lg:mb-3 border-b border-white/[0.04] lg:border-none max-lg:bg-transparent lg:bg-[#1a1a1b]">
                    <div className="flex items-center gap-3">
                        <Link to={authorLink} className="relative">
                            <Avatar className="h-10 w-10 ring-2 max-lg:ring-white/10 lg:ring-transparent transition-all hover:ring-white/30">
                                <AvatarImage src={authorAvatar} />
                                <AvatarFallback className={isTeamPost ? "bg-blue-900" : "bg-blue-600"}>
                                    {authorName?.substring(0, 2).toUpperCase()}
                                </AvatarFallback>
                            </Avatar>
                        </Link>
                        <div>
                            <Link to={authorLink} className="text-[15px] font-semibold text-zinc-100 hover:text-white transition-colors duration-200">
                                {authorName}
                            </Link>
                            <p className="text-xs text-zinc-500 font-medium">
                                {format(parseISO(post.created_at), 'MMM d, yyyy')}
                            </p>
                        </div>
                    </div>
                    {post.user_id === user?.id && (
                        <div className="relative">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setShowMenu(!showMenu)}
                                className="text-gray-400 hover:text-white h-8 w-8 p-0"
                            >
                                <MoreVertical className="h-5 w-5" />
                            </Button>
                            {showMenu && (
                                <>
                                    <div
                                        className="fixed inset-0 z-10"
                                        onClick={() => setShowMenu(false)}
                                    />
                                    <div className="absolute right-0 top-10 z-20 bg-[#1a1a1b] border border-gray-700 rounded-lg shadow-xl py-1 w-40 animate-in fade-in zoom-in duration-100">
                                        <button
                                            onClick={() => {
                                                setShowMenu(false);
                                                setIsEditing(true);
                                            }}
                                            className="w-full px-4 py-2 text-left text-sm text-white hover:bg-[#2a2a2b] flex items-center gap-2"
                                        >
                                            <Edit2 className="h-4 w-4" />
                                            {t('common.edit')}
                                        </button>
                                        {(post.video || post.image_url) && onFeature && (
                                            <button
                                                onClick={() => {
                                                    setShowMenu(false);
                                                    onFeature(post.id);
                                                }}
                                                className={`w-full px-4 py-2 text-left text-sm hover:bg-[#2a2a2b] flex items-center gap-2 ${post.is_featured ? 'text-yellow-500' : 'text-white'}`}
                                            >
                                                <Star className={`h-4 w-4 ${post.is_featured ? 'fill-current' : ''}`} />
                                                {post.is_featured ? (t('feed.removeHighlight') || 'Öne Çıkanlardan Kaldır') : (t('feed.addHighlight') || 'Öne Çıkar')}
                                            </button>
                                        )}
                                        <button
                                            onClick={() => {
                                                setShowMenu(false);
                                                onDelete(post.id);
                                            }}
                                            className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-[#2a2a2b] flex items-center gap-2"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                            {t('common.delete')}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                    {post.user_id !== user?.id && (
                        <div className="relative">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setShowMenu(!showMenu)}
                                className="text-gray-400 hover:text-white h-8 w-8 p-0"
                            >
                                <MoreVertical className="h-5 w-5" />
                            </Button>
                            {showMenu && (
                                <>
                                    <div
                                        className="fixed inset-0 z-10"
                                        onClick={() => setShowMenu(false)}
                                    />
                                    <div className="absolute right-0 top-10 z-20 bg-[#1a1a1b] border border-gray-700 rounded-lg shadow-xl py-1 w-40 animate-in fade-in zoom-in duration-100">
                                        {(user?.role === 'admin' || user?.role === 'ADMIN') && (post.video || post.image_url) && onFeature && (
                                            <button
                                                onClick={() => {
                                                    setShowMenu(false);
                                                    onFeature(post.id);
                                                }}
                                                className={`w-full px-4 py-2 text-left text-sm hover:bg-[#2a2a2b] flex items-center gap-2 ${post.is_featured ? 'text-yellow-500' : 'text-white'}`}
                                            >
                                                <Star className={`h-4 w-4 ${post.is_featured ? 'fill-current' : ''}`} />
                                                {post.is_featured ? (t('feed.removeHighlight') || 'Öne Çıkanlardan Kaldır') : (t('feed.addHighlight') || 'Öne Çıkar')}
                                            </button>
                                        )}
                                        {(user?.role === 'admin' || user?.role === 'ADMIN') && (
                                            <button
                                                onClick={() => {
                                                    setShowMenu(false);
                                                    onDelete(post.id);
                                                }}
                                                className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-[#2a2a2b] flex items-center gap-2"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                                {t('common.delete')}
                                            </button>
                                        )}
                                        <button
                                            onClick={() => {
                                                setShowMenu(false);
                                                onReport(post.id);
                                            }}
                                            className="w-full px-4 py-2 text-left text-sm text-orange-400 hover:bg-[#2a2a2b] flex items-center gap-2"
                                        >
                                            <Flag className="h-4 w-4" />
                                            {t('common.report')}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Media Section - Edge-to-edge on Mobile */}
                {post.video ? (
                    <div className="relative mb-0 lg:mb-3 bg-black lg:rounded-xl overflow-hidden flex justify-center w-full aspect-video lg:aspect-auto shadow-inner">
                        <SimpleVideoPlayer
                            src={(() => {
                                const rawUrl = post.video.video_url || post.video.url;
                                const urlWithToken = `${rawUrl}${rawUrl?.includes('/stream') ? `?token=${localStorage.getItem('token')}` : ''}`;
                                if (urlWithToken.startsWith('http')) return urlWithToken;
                                return `${BACKEND_URL}${urlWithToken}`;
                            })()}
                            className="w-full max-h-[85vh] lg:max-h-[80vh] object-contain"
                            poster={post.video.thumbnail_url?.startsWith('http') ? post.video.thumbnail_url : (post.video.thumbnail_url ? `${BACKEND_URL}${post.video.thumbnail_url}` : undefined)}
                        />
                        {/* Status Overlays (if needed) */}
                        {post.is_featured && (
                            <div className="absolute top-3 left-3 bg-yellow-500/90 backdrop-blur-md text-black text-[10px] font-black px-2 py-1 rounded-full flex items-center gap-1 shadow-lg ring-1 ring-black/10">
                                <Star className="w-3 h-3 fill-current" />
                                HIGHLIGHT
                            </div>
                        )}
                    </div>
                ) : post.image_url && (
                    <div className="relative mb-0 lg:mb-3 bg-[#0a0a0b] lg:rounded-xl overflow-hidden w-full aspect-square lg:aspect-auto flex items-center justify-center">
                        <img
                            src={post.image_url}
                            alt="Post content"
                            className="w-full h-full object-cover"
                        />
                    </div>
                )}

                {/* Caption Base Wrapper for Mobile Padding */}
                <div className="p-3 lg:p-0">
                    {/* Caption */}
                    {isEditing ? (
                        <div className="mb-3">
                            <textarea
                                value={editedCaption}
                                onChange={(e) => setEditedCaption(e.target.value)}
                                className="w-full bg-[#2a2a2b] border border-gray-700 rounded-lg p-3 text-white focus:ring-1 focus:ring-blue-500 resize-none"
                                rows={3}
                                maxLength={500}
                                autoFocus
                            />
                            <div className="flex gap-2 mt-2">
                                <Button
                                    size="sm"
                                    onClick={handleSaveEdit}
                                    className="bg-blue-600 hover:bg-blue-700"
                                    disabled={!editedCaption.trim()}
                                >
                                    <Check className="h-4 w-4 mr-1" /> {t('common.save')}
                                </Button>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={handleCancelEdit}
                                    className="text-gray-400 hover:text-white"
                                >
                                    {t('common.cancel')}
                                </Button>
                            </div>
                        </div>
                    ) : post.caption ? (
                        <ExpandableText text={post.caption} />
                    ) : null}

                    {/* Tags */}
                    {post.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3 mt-2">
                            {post.tags.map((tag, i) => (
                                <span key={i} className="text-blue-400 text-sm">#{tag}</span>
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-6 lg:gap-4 px-4 py-3 lg:py-0 lg:pt-3 lg:border-t lg:border-gray-800/60">
                    <div className="flex items-center">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onLike(post.id)}
                            className={`p-0 h-auto hover:bg-transparent ${post.is_liked ? 'text-rose-500' : 'text-zinc-400 lg:text-gray-400'} hover:text-rose-400 transition-all active:scale-75 duration-300 group`}
                        >
                            <Heart className={`h-7 w-7 lg:h-5 lg:w-5 mr-1.5 drop-shadow-md transition-transform group-hover:scale-110 ${post.is_liked ? 'fill-current' : ''}`} />
                        </Button>
                        <button
                            onClick={handleShowLikes}
                            className={`text-[16px] lg:text-sm font-bold tracking-tight hover:underline ${post.is_liked ? 'text-rose-500' : 'text-zinc-400 lg:text-gray-400'} hover:text-rose-400 transition-colors`}
                        >
                            {post.like_count}
                        </button>
                    </div>

                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowComments(!showComments)}
                        className="p-0 h-auto hover:bg-transparent text-zinc-400 lg:text-gray-400 hover:text-blue-400 transition-all active:scale-75 duration-300 group"
                    >
                        <MessageCircle className="h-7 w-7 lg:h-5 lg:w-5 mr-1.5 drop-shadow-md transition-transform group-hover:scale-110" />
                        <span className="text-[16px] lg:text-sm font-bold tracking-tight">{post.comment_count}</span>
                    </Button>

                    <div className="flex-1"></div>

                    {post.user_can_vote && (
                        <Button
                            variant="default"
                            size="sm"
                            onClick={() => onVote(post.id)}
                            className="bg-gradient-to-r from-yellow-500 to-amber-600 lg:bg-transparent text-black lg:text-yellow-500 hover:opacity-90 px-4 py-2 lg:p-0 h-9 lg:h-auto rounded-full lg:rounded-md flex items-center gap-2 font-black shadow-[0_4px_12px_rgba(245,158,11,0.3)] animate-in zoom-in duration-300 active:scale-95 transition-all"
                        >
                            <Trophy className="h-5 w-5 lg:h-5 lg:w-5 filter drop-shadow-md" />
                            <span className="uppercase text-[11px] lg:text-sm">{post.vote_count || 0} {t('common.vote', 'Oy')}</span>
                        </Button>
                    )}

                    {!post.user_can_vote && post.vote_count > 0 && (
                        <div className="flex items-center gap-2 text-yellow-500 lg:text-yellow-500/70 text-xs font-black bg-yellow-500/10 lg:bg-transparent px-4 py-2 lg:p-0 rounded-full border border-yellow-500/20 lg:border-none backdrop-blur-sm">
                            <Trophy className="h-5 w-5 lg:h-4 lg:w-4" />
                            <span className="uppercase">{post.vote_count} {t('common.vote', 'Oy')}</span>
                        </div>
                    )}
                </div>

                {/* Comments Section */}
                <div className="px-3 lg:px-0">
                    {!showComments && comments.length > 0 && (
                        <div className="mt-1 lg:mt-3 space-y-1.5 lg:space-y-2 pb-3 lg:pb-0 lg:pt-3 lg:border-t lg:border-gray-800/50">
                            {comments.length > 2 && (
                                <button
                                    onClick={() => setShowComments(true)}
                                    className="text-zinc-500 lg:text-gray-500 text-sm hover:text-zinc-300 mb-1 lg:mb-2 font-medium transition-colors"
                                >
                                    {t('feed.viewAllComments', { count: comments.length })}
                                </button>
                            )}
                            {comments.slice(-2).map((comment) => (
                                <div key={`preview-${comment.id}`} className="text-[14px] leading-snug">
                                    <Link to={`/profile/${comment.username}`} className="font-semibold text-zinc-100 lg:text-white mr-1.5 hover:underline decoration-white/50">
                                        {comment.username}
                                    </Link>
                                    <span className="text-zinc-300 lg:text-gray-400">{comment.text}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {showComments && (
                        <div className="pb-3 lg:pb-0 mt-3 lg:mt-4 lg:pt-4 border-t border-gray-800 lg:border-gray-700 animate-in fade-in slide-in-from-top-2">
                            {comments.map((comment) => (
                                <div key={comment.id} className="flex items-start gap-2 mb-3">
                                    <Link to={`/profile/${comment.username}`} className="text-blue-400 text-sm font-semibold hover:underline">
                                        {comment.username}
                                    </Link>
                                    <p className="text-zinc-300 lg:text-gray-300 text-[14px] flex-1 leading-snug">{comment.text}</p>
                                    {((comment.username === user?.username) || user?.role === 'admin' || user?.role === 'ADMIN') && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleDeleteComment(comment.id)}
                                            className="text-zinc-500 hover:text-rose-500 h-6 w-6 p-0 transition-colors"
                                        >
                                            <Trash2 className="h-4 w-4 lg:h-3 lg:w-3" />
                                        </Button>
                                    )}
                                </div>
                            ))}

                            <form onSubmit={handleSubmitComment} className="flex gap-2 mt-4 lg:mt-3">
                                <Input
                                    value={newComment}
                                    onChange={(e) => setNewComment(e.target.value)}
                                    placeholder={t('feed.addComment', 'Add a comment...')}
                                    className="bg-white/5 lg:bg-[#2a2a2b] border-white/10 lg:border-gray-700 text-white flex-1 h-10 lg:h-9 focus-visible:ring-1 focus-visible:ring-blue-500/50 placeholder:text-zinc-500"
                                    maxLength={300}
                                />
                                <Button type="submit" size="sm" className="h-10 w-10 lg:h-9 lg:w-auto px-0 lg:px-3 rounded-full lg:rounded-md bg-blue-600 hover:bg-blue-500 text-white active:scale-95 transition-all shadow-[0_0_10px_rgba(37,99,235,0.2)]" disabled={!newComment.trim() || submitting}>
                                    <Send className="h-4 w-4 ml-0 lg:ml-0 -ml-0.5" />
                                </Button>
                            </form>
                        </div>
                    )}
                </div>
            </CardContent>

            <UserListModal
                isOpen={likesModal.isOpen}
                onClose={() => setLikesModal(prev => ({ ...prev, isOpen: false }))}
                title={t('common.likes') || "Beğenenler"}
                users={likesModal.users}
                loading={likesModal.loading}
            />
        </Card>
    );
};

export default PostCard;
